import {
  WORKPLACE_REQUEST_RESERVATION_MAX_DATES,
  type WorkplaceRequestReservationAttendeeInput,
  type WorkplaceRequestReservationWorkflowOverride,
  type WorkplaceRequestReservationWorkflowValues
} from "@gettactic/api";
import {
  time12To24,
  time24To12
} from "@gettactic/helpers/src/date-time/timeStringConverters";
import { format, parse } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { z } from "zod";

/**
 * The approval modal's form, and the two pure conversions around it:
 * `resolved` (what core would book) → form values, and form values → the
 * override to send. Days and times are edited as wall-clock in `tz` and
 * converted back to instants on the way out.
 */

export const RESERVATION_BUFFER_MAX_MINUTES = 2880;

const DATE_FORMAT = "yyyy-MM-dd";

/** `YYYY-MM-DDTHH:MM` — sortable wall-clock, no zone. */
const wallClock = (date: Date, time12: string): string =>
  `${format(date, DATE_FORMAT)}T${time12 ? time12To24(time12) : "00:00"}`;

const optionalMinutes = z
  .string()
  .refine(
    (value) =>
      value.trim() === "" ||
      (/^\d+$/.test(value.trim()) &&
        Number(value) <= RESERVATION_BUFFER_MAX_MINUTES),
    `Whole minutes between 0 and ${RESERVATION_BUFFER_MAX_MINUTES}`
  );

/**
 * One reservation to book, as the approver edits it.
 *
 * A day plus two times, rather than the two independent instants core stores:
 * nothing in Tactic books a window across midnight — the reserve form won't
 * offer one either — and one row per day stays readable where fifteen rows of
 * four controls would not.
 */
const reservationPeriodSchema = z.object({
  date: z.date({ error: "Pick a date" }),
  startTime: z.string().min(1, "Pick a start time"),
  endTime: z.string().min(1, "Pick an end time")
});

export type ReservationPeriodFormValue = z.infer<
  typeof reservationPeriodSchema
>;

export const reservationOverrideSchema = z
  .object({
    resourceId: z.string().min(1, "Pick a reservation room or desk"),
    organizerId: z.string().min(1, "Pick a reservation organizer"),
    periods: z
      .array(reservationPeriodSchema)
      .min(1, "Keep at least one day, or cancel and approve without booking")
      .max(
        WORKPLACE_REQUEST_RESERVATION_MAX_DATES,
        `Book at most ${WORKPLACE_REQUEST_RESERVATION_MAX_DATES} days at once`
      ),
    title: z.string(),
    /** HTML. The rich-text editor reports an emptied field as `null`. */
    description: z.string().nullable(),
    attendeeUserIds: z.array(z.string()),
    /**
     * Guests the request itself named, by address. There is no control to add
     * one — they arrive with the reservation — but they are carried through
     * the form so that editing the people below doesn't drop them, since an
     * `attendees` override replaces the whole list.
     */
    attendeeEmails: z.array(z.string()),
    onlineMeeting: z.boolean(),
    bufferBefore: optionalMinutes,
    bufferAfter: optionalMinutes
  })
  .superRefine((values, ctx) => {
    values.periods.forEach((period, index) => {
      if (
        wallClock(period.date, period.endTime) <=
        wallClock(period.date, period.startTime)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["periods", index, "endTime"],
          message: "This day has to end after it starts"
        });
      }
    });

    // Core refuses overlapping windows, and applies the buffer before it
    // checks; the plain overlap is what an approver actually stumbles into
    // (the same day twice), so it is the one worth naming here by row.
    values.periods.forEach((period, index) => {
      const start = wallClock(period.date, period.startTime);
      const end = wallClock(period.date, period.endTime);
      const overlaps = values.periods.some(
        (other, otherIndex) =>
          otherIndex < index &&
          wallClock(other.date, other.startTime) < end &&
          wallClock(other.date, other.endTime) > start
      );
      if (overlaps) {
        ctx.addIssue({
          code: "custom",
          path: ["periods", index, "startTime"],
          message: "This overlaps another day above"
        });
      }
    });

    const hasBefore = values.bufferBefore.trim() !== "";
    const hasAfter = values.bufferAfter.trim() !== "";
    if (hasBefore !== hasAfter) {
      ctx.addIssue({
        code: "custom",
        path: [hasBefore ? "bufferAfter" : "bufferBefore"],
        message: "Set both buffers, or neither"
      });
    }
  });

export type ReservationOverrideFormValues = z.infer<
  typeof reservationOverrideSchema
>;

/** The wall-clock instant in `tz` as an RFC3339 UTC string. */
const toInstant = (date: Date, time12: string, tz: string): string =>
  fromZonedTime(`${wallClock(date, time12)}:00`, tz).toISOString();

const periodToInstants = (
  period: ReservationPeriodFormValue,
  tz: string
): { start: string; end: string } => ({
  start: toInstant(period.date, period.startTime, tz),
  end: toInstant(period.date, period.endTime, tz)
});

const sameInstant = (a: string, b: string): boolean =>
  new Date(a).getTime() === new Date(b).getTime();

/**
 * Periods are ordered — core books them in the order it is given — so this
 * compares position by position rather than as a set.
 */
const samePeriods = (
  a: { start: string; end: string }[],
  b: { start: string; end: string }[]
): boolean =>
  a.length === b.length &&
  a.every(
    (period, index) =>
      sameInstant(period.start, b[index].start) &&
      sameInstant(period.end, b[index].end)
  );

/** How an attendee reads as a single string, for comparing two lists. */
const attendeeKey = (
  attendee: WorkplaceRequestReservationAttendeeInput
): string => attendee.user_id ?? attendee.email ?? "";

const sameAttendees = (
  a: WorkplaceRequestReservationAttendeeInput[],
  b: WorkplaceRequestReservationAttendeeInput[]
): boolean => {
  const sortedA = a.map(attendeeKey).sort();
  const sortedB = b.map(attendeeKey).sort();
  return (
    sortedA.length === sortedB.length &&
    sortedA.every((key, index) => key === sortedB[index])
  );
};

/**
 * The people first, then the guests the request named. Order only matters for
 * the diff above, which sorts, so this keeps the two groups readable instead.
 */
const toAttendees = (
  values: Pick<
    ReservationOverrideFormValues,
    "attendeeUserIds" | "attendeeEmails"
  >
): WorkplaceRequestReservationAttendeeInput[] => [
  ...values.attendeeUserIds.map((user_id) => ({ user_id })),
  ...values.attendeeEmails.map((email) => ({ email }))
];

export const emptyReservationFormValues =
  (): ReservationOverrideFormValues => ({
    resourceId: "",
    organizerId: "",
    periods: [],
    title: "",
    description: "",
    attendeeUserIds: [],
    attendeeEmails: [],
    onlineMeeting: false,
    bufferBefore: "",
    bufferAfter: ""
  });

/**
 * Prefills the form from what core resolved, as wall-clock in `tz`. Each day is
 * a local `Date` at midnight, which is what the calendar input stores — it only
 * ever reads the day back, never the instant.
 *
 * A period is read as a single day: its end time is taken from the same day its
 * start falls on, which is the only shape core produces (one reservation per
 * day, sharing one window).
 */
export const resolvedToFormValues = (
  resolved: WorkplaceRequestReservationWorkflowValues | undefined,
  tz: string
): ReservationOverrideFormValues => {
  if (!resolved) return emptyReservationFormValues();
  const day = (instant: string): Date =>
    parse(formatInTimeZone(instant, tz, DATE_FORMAT), DATE_FORMAT, new Date());
  const clock = (instant: string): string =>
    time24To12(formatInTimeZone(instant, tz, "HH:mm"));
  const attendees = resolved.attendees ?? [];
  return {
    resourceId: resolved.resource_id,
    organizerId: resolved.organizer_id,
    periods: (resolved.periods ?? []).map((period) => ({
      date: day(period.start),
      startTime: clock(period.start),
      endTime: clock(period.end)
    })),
    title: resolved.title ?? "",
    description: resolved.description ?? "",
    attendeeUserIds: attendees
      .map((attendee) => attendee.user_id)
      .filter((userId): userId is string => !!userId),
    attendeeEmails: attendees
      .map((attendee) => attendee.email)
      .filter((email): email is string => !!email),
    onlineMeeting: resolved.online_meeting ?? false,
    bufferBefore: resolved.buffer ? String(resolved.buffer.before_minutes) : "",
    bufferAfter: resolved.buffer ? String(resolved.buffer.after_minutes) : ""
  };
};

/**
 * Only what differs from `resolved` is sent, so an untouched form sends nothing
 * and core books exactly what it resolved. Without `resolved` (core couldn't
 * resolve the ticket) every value the approver set is sent.
 *
 * `recurrence` is never sent: the form has no control for it, and omitting a
 * key is what keeps the resolved value. Sending one back unchanged would only
 * risk core reading it as a change.
 *
 * Returns `null` when there is nothing to override.
 */
export const formValuesToOverride = (
  values: ReservationOverrideFormValues,
  resolved: WorkplaceRequestReservationWorkflowValues | undefined,
  tz: string
): WorkplaceRequestReservationWorkflowOverride | null => {
  const periods = values.periods.map((period) => periodToInstants(period, tz));
  const attendees = toAttendees(values);
  const title = values.title.trim();
  const description = (values.description ?? "").trim();
  const hasBuffer =
    values.bufferBefore.trim() !== "" && values.bufferAfter.trim() !== "";
  const buffer = hasBuffer
    ? {
        before_minutes: Number(values.bufferBefore),
        after_minutes: Number(values.bufferAfter)
      }
    : undefined;

  const override: WorkplaceRequestReservationWorkflowOverride = {};

  if (!resolved) {
    override.periods = periods;
    override.resource_id = values.resourceId;
    override.organizer_id = values.organizerId;
    if (title) override.title = title;
    if (description) override.description = description;
    if (attendees.length > 0) override.attendees = attendees;
    override.online_meeting = values.onlineMeeting;
    if (buffer) override.buffer = buffer;
    return override;
  }

  if (!samePeriods(periods, resolved.periods ?? [])) {
    override.periods = periods;
  }
  if (values.resourceId !== resolved.resource_id) {
    override.resource_id = values.resourceId;
  }
  if (values.organizerId !== resolved.organizer_id) {
    override.organizer_id = values.organizerId;
  }
  if (title !== (resolved.title ?? "")) override.title = title;
  if (description !== (resolved.description ?? "")) {
    override.description = description;
  }
  if (!sameAttendees(attendees, resolved.attendees ?? [])) {
    override.attendees = attendees;
  }
  if (values.onlineMeeting !== (resolved.online_meeting ?? false)) {
    override.online_meeting = values.onlineMeeting;
  }
  if (
    buffer &&
    (buffer.before_minutes !== resolved.buffer?.before_minutes ||
      buffer.after_minutes !== resolved.buffer?.after_minutes)
  ) {
    override.buffer = buffer;
  }

  return Object.keys(override).length > 0 ? override : null;
};
